import { type UserSession } from '@solinkcorp/authentication';
import { type VideoSegment } from '@solinkcorp/playlist-parser';
import {
  add,
  endOfDay,
  getAccessibleAddress,
  getLocalTimeZone,
  startOfDay,
} from '@solinkcorp/utils';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import * as VideoUtil from 'src/core/video/VideoUtil';
import { type SelectedPlayListSource } from 'utils/types';
import WOSCache from 'utils/WOSCache';
import { type PlaylistCache } from './PlaylistCache';

const MAX_DAYS_TO_CACHE = 1000;

const HOUR_IN_MS = 60 * 60 * 1000;

// This is used as a quick check to see if a query is part of "today".
// 25 hours because daylight saving time.  If an entry in the cache is from
// today, we'll check for new segments every 5 minutes.
const MAX_TODAY_AGE = 25 * HOUR_IN_MS;

// Revalidate segment data every 2 minutes.
export const REVALIDATE_TODAY_PERIOD = 2 * 60 * 1000;

export interface CachedSegmentQuery {
  user: UserSession;
  /** Cameras to load motion segments for. */
  playingCameras: PlayingCamera[];
  selectedPlayListSource: SelectedPlayListSource;
  start: number;
  end: number;
  localLocations: Record<string, boolean | undefined>;
}

export interface CachedSegmentData {
  segments: readonly VideoSegment[];
  locationId: string;
  vmsCameraId: string;
  streamId: string;
  id: string;
  start: number;
  end: number;
}

/**
 * A CacheBucketDescriptor contains parameters required to fetch the contents of
 * a given cache bucket, either from the cache (via the `key`) or from the
 * PlatformAPI.
 */
interface SegmentCacheBucketDescriptor {
  /** The key of this bucket. */
  key: string;
  /** The query parameters used to generate the data in this bucket. */
  params: VideoUtil.RecordingUrlParams;
}

export interface SegmentCacheResult {
  [locationId: string]:
    | {
        [vmsCameraId: string]: readonly CachedSegmentData[] | undefined;
      }
    | undefined;
}

/**
 * SegmentCache caches motion segments for
 * per-location basis, so this cache is intended to be used when showing event
 * from a specific location over a specific time period (and ideally from a
 * specific camera).  For example, this is used by the video player to
 * render events in the timeline and to render events near the current event.
 *
 * A given cache bucket holds data from a specific day, from a specific location,
 * and for any given search queries (searchString, searchCapsules, and selectedReportId
 * will all generate different cache buckets, mainly because we don't want to
 * re-implement these searches client-side).
 *
 * This cache currently does not support caching events across locations, so it
 * can't be used in places like the "events" page where we need to show
 * data across multiple locations.  Caching data across locations would be
 * difficult just because of the sheer quantity of events involved.
 *
 * Cached results that are "recent" will use a "stale-while-revalidate" strategy;
 * events from the cache will be returned immediately, but in the background we'll
 * go check with platform API to see if any new events have snuck in.  For older
 * buckets in the cache, we won't bother to revalidate them, because older events
 * are unlikely to change.
 */
export default class SegmentCache {
  private playlistCache: PlaylistCache;
  private _cache: WOSCache<
    CachedSegmentData,
    CachedSegmentQuery,
    SegmentCacheBucketDescriptor,
    SegmentCacheResult
  >;

  /**
   * The number of times we refreshed a stale cache with data from the server.
   */
  public cacheRefreshes = 0;

  /**
   * The number of times we successfully refreshed the cache by fetching
   * all the new events at the end of "today" without having to re-fetch data.
   */
  public cacheTodayRefreshes = 0;

  /**
   * Create a new SegmentCache.
   *
   * @params [options.maxSize] - The maximum number of days worth of segments to cache.
   */
  constructor(
    playlistCache: PlaylistCache,
    options: {
      maxSize?: number;
    } = {},
  ) {
    // TODO: Proactively listen to playlistCache so when the video player loads
    // new segments, we get them "for free".
    this.playlistCache = playlistCache;

    this._cache = new WOSCache({
      name: 'segment cache',
      max: options.maxSize ?? MAX_DAYS_TO_CACHE,
      length: () => 1,
      fetchFunction: this._fetchFunction,
      keyFunction: (descriptor: SegmentCacheBucketDescriptor) => descriptor.key,
      getRevalidationTimeout,
      queryMapper: getQueriesForCamera,
      resultMapper: collateResults,
    });
  }

  private _fetchFunction = async (
    descriptor: SegmentCacheBucketDescriptor,
    oldEntry: CachedSegmentData | undefined,
  ) => {
    if (!oldEntry) {
      return await this._fetchNewCacheEntry(descriptor);
    }
    return await this._refreshExistingCacheEntry(descriptor, oldEntry);
  };

  /**
   * Subscribe to events from the cache.
   *
   * This will call the provided `listener`  with data from the cache as soon
   * as some is available, and then call it again whenever data in the cache is
   * updated.
   *
   * @returns a function to call to stop receiving updates for this query.  This
   *   must be called, or we'll query PlatformApi forever!
   *
   * @example
   *     const unsubscribe = eventCache.subscribe((err, events) => console.log(events));
   *     unsubscribe(); // Call when done with the subscription.
   */
  subscribe(
    query: CachedSegmentQuery,
    listener: (err: Error | undefined, segments: SegmentCacheResult | undefined) => void,
  ): () => void {
    return this._cache.subscribe(query, listener);
  }

  /**
   * Get events from the cache.
   */
  getSegments(query: CachedSegmentQuery): Promise<SegmentCacheResult> {
    return this._cache.get(query);
  }

  private async _fetchNewCacheEntry(
    descriptor: SegmentCacheBucketDescriptor,
  ): Promise<CachedSegmentData> {
    const params = descriptor.params;
    const vmsCameraId = params.vmsCameraId;
    const locationId = params.location.id;

    const urlProps = VideoUtil.generateRecordingURL(params);

    if (urlProps === undefined) {
      throw new Error(`Cannot load timeline for restricted camera`);
    }

    const { playlist } = await this.playlistCache.loadPlaylist(urlProps);

    return {
      id: VideoUtil.getSegmentsId(params),
      locationId,
      vmsCameraId,
      streamId: params.streamId,
      segments: playlist.segments,
      start: params.start,
      end: params.end,
    };
  }

  /**
   * This will check to see of a given `EventCacheData` is up to date.  If
   * the server has no new events, this will return a new cache entry with the
   * exact same events object and a new fetch time.  If there's new data available,
   * this will fetch the new data.
   */
  private async _refreshExistingCacheEntry(
    descriptor: SegmentCacheBucketDescriptor,
    cacheData: CachedSegmentData,
  ): Promise<CachedSegmentData> {
    // Fetch any segments after the last segment in the cache.
    const query = { ...descriptor.params };

    let lastSegment: VideoSegment | undefined;

    if (cacheData.segments.length) {
      lastSegment = cacheData.segments[cacheData.segments.length - 1];
      query.start = lastSegment.end;
    }

    const urlProps = VideoUtil.generateRecordingURL(query);

    if (urlProps === undefined) {
      // TODO: Move this error into `generateRecordingURL`?
      throw new Error(`Cannot load timeline for restricted camera`);
    }

    const { playlist } = await this.playlistCache.loadPlaylist(urlProps);
    let newSegments = playlist.segments;

    // If there are any segments at the start of `newSegments` that we already have,
    // discard them.
    if (lastSegment !== undefined) {
      const lastStart = lastSegment.start;
      const firstIndex = newSegments.findIndex((segment) => segment.start > lastStart);
      if (firstIndex === -1) {
        newSegments = [];
      } else if (firstIndex > 0) {
        newSegments = newSegments.slice(firstIndex);
      }
    }

    return {
      ...cacheData,
      segments: cacheData.segments.concat(newSegments),
    };
  }
}

/**
 * Retunrs time in milliseconds until this entry should be revalidated, or undefined
 * if the entry does not need to be revalidated.
 */
function getRevalidationTimeout(
  _descriptor: SegmentCacheBucketDescriptor,
  entry: CachedSegmentData,
) {
  if (entry.start > Date.now() - MAX_TODAY_AGE) {
    // Entry is from today.
    return REVALIDATE_TODAY_PERIOD;
  }
  // Motion segments never change, so don't bother revalidating anything from before today.
  return undefined;
}

function getQueriesForCamera(query: CachedSegmentQuery): SegmentCacheBucketDescriptor[] {
  const { playingCameras, selectedPlayListSource, start, end, localLocations } = query;

  const result: SegmentCacheBucketDescriptor[] = [];

  for (const playingCamera of playingCameras) {
    const { location, camera, device } = playingCamera;
    const timeZoneId = location.timezone ?? getLocalTimeZone();

    let currentDay = start;
    while (currentDay < end) {
      const start = startOfDay(currentDay, timeZoneId).valueOf();
      const end = endOfDay(currentDay, timeZoneId).valueOf();

      const streamInfo = VideoUtil.selectStream({
        location: device,
        streams: camera.streams,
        // When loading segments from a camera, use the `end` time to pick a stream.
        // The problem here is that if we want to load segments for a camera that
        // was just added today, the `stream.earliestSegmentDate` will be before
        // `start`.  `selectStream()` in this case will fall back to getting
        // segments from the cloud, which is probably not what we want.  If there
        // are any segments available for the given day, we want those segments.
        targetStartTime: end,
        quality: 'sd',
        selectedPlayListSource,
        isDeviceOffline: false,
        prioritizeMotionStream: true,
      });

      if (streamInfo) {
        const accessibleAddress =
          getAccessibleAddress(camera, localLocations[location.id]) ?? undefined;
        const params: VideoUtil.RecordingUrlParams = {
          user: query.user,
          quality: 'sd',
          location: {
            ...location,
            accessibleAddress,
          },
          source: streamInfo.source,
          streamId: streamInfo.stream.id,
          start,
          end,
          vmsCameraId: camera.cameraId,
          selectedPlayListSource,
          videoUrl: camera.videoUrl, // For alarm events (VerifEye3): alarm.m3u8 URL
        };

        result.push({
          key: VideoUtil.getSegmentsId(params),
          params,
        });
      }

      currentDay = add(currentDay, timeZoneId, 1, 'day').valueOf();
    }
  }

  return result;
}

function collateResults(
  query: CachedSegmentQuery,
  results: CachedSegmentData[],
): SegmentCacheResult {
  const result: {
    [locationId: string]: {
      [vmsCameraId: string]: CachedSegmentData[];
    };
  } = {};

  for (const entry of results) {
    const locationResult = (result[entry.locationId] ||= {});
    const cameraResult = (locationResult[entry.vmsCameraId] ||= []);
    cameraResult.push(entry);
  }

  return result;
}
